/**
 * Terreiro Signup Repository
 *
 * Handles all API calls related to terreiro signup/onboarding.
 * Supports both complete (4-step) and quick (2-step) signup modes.
 */

import { axiosClient } from "../api/axiosClient";
import type {
  QuickTerreiroSignupPayload,
  QuickTerreiroSocialSignupPayload,
  QuickTerreiroSignupResponse,
  QuickTerreiroSocialSignupResponse,
  TerreiroSignupPayload,
} from "../types/terreiroSignup.types";

export const terreiroSignupQueryKeys = {
  signup: ["terreiroSignup"] as const,
  quickSignup: ["terreiroQuickSignup"] as const,
};

export const terreiroSignupRepository = {
  /**
   * Complete terreiro signup - creates account with all fields from 4-step onboarding.
   * Uses /terreiro-signup endpoint.
   */
  async signup(
    payload: TerreiroSignupPayload,
    attempt = 1,
  ): Promise<{ msg: string; success: boolean }> {
    try {
      const res = await axiosClient.post(`/terreiro-signup`, payload, {
        withCredentials: true,
      });
      return res.data;
    } catch (err: unknown) {
      interface AxiosErrorWithResponse {
        response?: {
          status?: number;
        };
      }
      // Retry on 5xx errors
      if (
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        typeof (err as AxiosErrorWithResponse).response === "object"
      ) {
        const response = (err as AxiosErrorWithResponse).response;
        if (
          response?.status !== undefined &&
          response.status >= 500 &&
          attempt < 3
        ) {
          await new Promise((r) => setTimeout(r, 2 ** attempt * 500));
          return await terreiroSignupRepository.signup(payload, attempt + 1);
        }
      }
      throw err;
    }
  },

  /**
   * Quick terreiro signup - creates account with minimal required fields.
   * Uses /terreiro-signup/quick endpoint.
   *
   * Creates:
   * - User account
   * - Profile
   * - Contact
   * - Terreiro
   * - Medium (as leader)
   * - Default cash account
   * - Default monthly fee settings
   */
  async quickSignup(
    payload: QuickTerreiroSignupPayload,
    attempt = 1,
  ): Promise<QuickTerreiroSignupResponse> {
    try {
      const res = await axiosClient.post<QuickTerreiroSignupResponse>(
        `/terreiro-signup/quick`,
        payload,
        {
          withCredentials: true,
        },
      );
      return res.data;
    } catch (err: unknown) {
      interface AxiosErrorWithResponse {
        response?: {
          status?: number;
        };
      }
      // Retry on 5xx errors
      if (
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        typeof (err as AxiosErrorWithResponse).response === "object"
      ) {
        const response = (err as AxiosErrorWithResponse).response;
        if (
          response?.status !== undefined &&
          response.status >= 500 &&
          attempt < 3
        ) {
          await new Promise((r) => setTimeout(r, 2 ** attempt * 500));
          return await terreiroSignupRepository.quickSignup(
            payload,
            attempt + 1,
          );
        }
      }
      throw err;
    }
  },

  async quickSignupSocial(
    payload: QuickTerreiroSocialSignupPayload,
    attempt = 1,
  ): Promise<QuickTerreiroSocialSignupResponse> {
    try {
      const res = await axiosClient.post<QuickTerreiroSocialSignupResponse>(
        `/terreiro-signup/quick/authenticated`,
        payload,
        {
          withCredentials: true,
        },
      );
      return res.data;
    } catch (err: unknown) {
      interface AxiosErrorWithResponse {
        response?: {
          status?: number;
        };
      }
      if (
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        typeof (err as AxiosErrorWithResponse).response === "object"
      ) {
        const response = (err as AxiosErrorWithResponse).response;
        if (
          response?.status !== undefined &&
          response.status >= 500 &&
          attempt < 3
        ) {
          await new Promise((r) => setTimeout(r, 2 ** attempt * 500));
          return await terreiroSignupRepository.quickSignupSocial(
            payload,
            attempt + 1,
          );
        }
      }
      throw err;
    }
  },
};
